import React, { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import s from './Profile.module.scss';
import { FormattedMessage, useIntl } from 'react-intl';
import Banner from '../Banner/Banner';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { v4 as uuidv4 } from 'uuid';
import ProfileWidgets from './ProfileWidgets';
import SearchBar from '../SearchBar/SearchBar';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { isRequired, compareValues, groupBy, sortObjectKeys } from '../../utils/';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import classNames from 'classnames';
import Dropdown from '../WSAControls/DroprdownContainer/Dropdown';
import AdminControlsContentTemplate from '../AdminControls/AdminControlsContentTemplate';
import { SELECT_ALL } from '../../constants/index';
import Cookies from 'js-cookie';
import ProfileWidgetCategory from './ProfileWidgetCategory';
import { IsWidgetAccessible } from '../../utils/widgetManager';


const envName = process.env.REACT_APP_ENV_NAME_SHORT;
const Profile = ({
  profile = {
    profiles: [],
    selectedProfile: {
      ProfileId: 0,
      Name: '',
      CreatedOrganisationId: 0,
      Description: '',
      GuidIdentifier: '',
      IsDefault: false,
      IsActive: true,
      ProfileWigets: []
    },
    isLoading: false,
    showBanner: false,
    isOpSuccessful: true,
    redirect: false,
    errorMessage: '',
    isError: false
  },
  selectedListStateProfile = {},
  widgetData = {
    appWidgets: []
  },
  saveProfile,
  setSelectedProfile,
  getSelectedProfile,
  getAppWidgets,
  userProfileWidgets
}) => {
  const intl = useIntl();
  const params = useParams();
  const envName = process.env.REACT_APP_ENV_NAME_SHORT;

  const [profileInfo, setProfileInfo] = useState({
    Name: '',
    Description: '',
    IsActive: true,
    IsMFAEnabled: false,
    GuidIdentifier: uuidv4(),
    IsDefault: false,
    ProfileId: 0,
    ProfileWigets: []
  });

  const [isEdit, setIsEdit] = useState(false);
  const [selectedWidgets, setSelectedWidgets] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [filterText, setFilterText] = useState('');
  const [filterScreenName, setFilterScreenName] = useState('');
  const currentOrganisationId = Cookies.get(`selectedorganisationid-${envName}`) || 0;
  const isDuplicateAllowed = IsWidgetAccessible(userProfileWidgets, 'PROFILEDUPLICATE');
  let isDuplicate = params.duplicate && params.duplicate === 'true';


  // Group by screen name helper
  const groupByScreenName = groupBy('ScreenName');

  useEffect(() => {
    let profileId = params.profileId;



    let widgetCode = profileId ? 'PROFILEUPDATE' : isDuplicate ? 'PROFILEDUPLICATE' : 'PROFILEINSERT';
    getAppWidgets(widgetCode);

    if (profileId) {
      getSelectedProfile(profileId, isDuplicate, widgetCode);

      let selProfile = profile.selectedProfile;

      setProfileInfo({
        Name: selProfile.Name || '',
        Description: selProfile.Description || '',
        IsMFAEnabled: selProfile.IsMFAEnabled || false,
        IsActive: selProfile.IsActive,
        GuidIdentifier: selProfile.GuidIdentifier,
        ProfileId: selProfile.ProfileId,
        IsDefault: selProfile.IsDefault,
        ProfileWigets: []
      });

      setFormErrors({});
      setIsEdit(true);
      setSelectedWidgets([]);
    } else {
      let currentProfile = profile.selectedProfile;
      currentProfile.GuidIdentifier = uuidv4();
      currentProfile.IsDefault = false;

      setProfileInfo(currentProfile);
      setIsEdit(false);
      setSelectedWidgets([]);
      setFormErrors({});
    }
  }, [ params.profileId, isDuplicate]);

  // Update when selected profile changes
  useEffect(() => {
    if (profile.selectedProfile && profile.selectedProfile.ProfileId !== profileInfo.ProfileId) {
      let profileWigets = [];
      if (profile.selectedProfile.ProfileWigets && profile.selectedProfile.ProfileWigets.length > 0) {
        profileWigets = profile.selectedProfile.ProfileWigets.map(a => a.WidgetId);
      }

      setProfileInfo(profile.selectedProfile);
      setSelectedWidgets(profileWigets);
    }
  }, [profile.selectedProfile]);

  const onChange = (event) => {
    const { name, value, checked, type } = event.target;

    setProfileInfo(prevProfile => ({
      ...prevProfile,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const submitForm = (e) => {
    e.preventDefault();

    if (!validateInput()) {
      return;
    }

    let data = { ...profileInfo };
    if (isDuplicate) {
      data.ProfileId = 0;
      data.GuidIdentifier = uuidv4();
      data.IsDuplicate = true;
    }
    data.UpdatedBy = Cookies.get(`userid-${envName}`) || 1;
    data.ProfileWigets = selectedWidgets;
    data.OrganisationId = Cookies.get(`selectedorganisationid-${envName}`) || 0;

    // Adding NAV_HOME if not included
    if (!data.ProfileWigets.includes(19)) data.ProfileWigets.push(19);

    // Adding NAV_SETTINGS if not included
    if (!data.ProfileWigets.includes(22)) data.ProfileWigets.push(22);

    let widgetCode = data.ProfileId ? 'PROFILEUPDATE' : isDuplicate ? 'PROFILEDUPLICATE' : 'PROFILEINSERT';
    saveProfile(data, widgetCode);
  };

  const validateInput = () => {
    let errors = {};
    let isValid = true;

    scrollToTop();

    if (!profileInfo || !profileInfo.Name || isRequired(profileInfo.Name, 2)) {
      errors.Name = intl.formatMessage({
        id: 'profile.validationNameLength',
        defaultMessage: 'Profile Name must be atleast 2 characters long'
      });
      isValid = false;
    }

    if (!profileInfo || !profileInfo.Name || isRequired(profileInfo.Name, 1)) {
      errors.Name = intl.formatMessage({
        id: 'profile.validationName',
        defaultMessage: 'Profile Name is required!'
      });
      isValid = false;
    }

    if (!profileInfo || !profileInfo.Description || isRequired(profileInfo.Description, 1)) {
      errors.Description = intl.formatMessage({
        id: 'profile.validationDescriptionLength',
        defaultMessage: 'Profile Description is required!'
      });
      isValid = false;
    }

    if (!profileInfo || (!profileInfo.IsActive && profileInfo.IsMFAEnabled)) {
      errors.ProfileMFA = intl.formatMessage({
        id: 'profile.validationActiveProfileMFA',
        defaultMessage: 'MFA must be disabled if profile is not active!'
      });
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const onWidgetChange = (widgets, isDelete) => {
    let newSelectedWidgets = [...selectedWidgets];

    if (!isDelete) {
      widgets.forEach(id => {
        if (!newSelectedWidgets.includes(id)) {
          newSelectedWidgets.push(id);
        }
      });
    } else {
      widgets.forEach(id => {
        const removeIndex = newSelectedWidgets.indexOf(id);
        if (removeIndex > -1) {
          newSelectedWidgets.splice(removeIndex, 1);
        }
      });
    }

    setSelectedWidgets(newSelectedWidgets);
  };

  const updateProfileAttributes = () => {
    setSelectedProfile(profileInfo);
  };

  const searchHandler = (searchQuery) => {
    setFilterText(searchQuery);
  };

  const screenNameOptions = (groupWidgets) => {
    let screenNamesData = [];
    screenNamesData.push({
      label: SELECT_ALL,
      value: '0'
    });

    if (filterText === '') {
      widgetData.appWidgets.forEach(element => {
        if (!screenNamesData.some(x => x.label === element.ScreenName)) {
          screenNamesData.push({
            label: element.ScreenName,
            value: element.ScreenId
          });
        }
      });
    } else {
      Object.keys(groupWidgets).forEach(element => {
        if (!screenNamesData.some(x => x.label === element)) {
          screenNamesData.push({
            label: element,
            value: groupWidgets[element].ScreenId
          });
        }
      });
    }

    return screenNamesData.sort(compareValues('label'));
  };

  const onScreenNameChange = (e) => {
    setFilterScreenName(e.label);
  };

  const getSelectedWidgets = (widgets) => {
    let selectedGroupWidgets = [];
    widgets.forEach(item => {
      if (selectedWidgets.includes(item.WidgetId)) {
        selectedGroupWidgets.push(item.WidgetId);
      }
    });
    return selectedGroupWidgets;
  };

  const getActiveWidgets = (groupWidgets) => {
    var filteredWidgets = [];

    if (filterText !== '') {
      filteredWidgets = Object.keys(groupWidgets).reduce(function (accumulator, currentValue) {
        var widgets = groupWidgets[currentValue].filter(function (widget) {
          return widget.Name.toLowerCase().includes(filterText.toLowerCase());
        });
        if (widgets.length > 0 && (filterScreenName === '' || filterScreenName === SELECT_ALL || currentValue === filterScreenName)) {
          accumulator[currentValue] = widgets;
        }
        return accumulator;
      }, {});
    } else {
      if (filterScreenName === '' || filterScreenName === SELECT_ALL) {
        filteredWidgets = groupWidgets;
      } else {
        filteredWidgets = Object.keys(groupWidgets).reduce(function (accumulator, currentValue) {
          if (currentValue === filterScreenName) {
            accumulator[currentValue] = groupWidgets[currentValue];
          }
          return accumulator;
        }, {});
      }
    }
    return filteredWidgets;
  };

  const scrollToTop = () => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };

  const { Name, Description, IsMFAEnabled, IsActive } = profileInfo;
  const { isLoading, redirect } = profile;

  let messageId = (profile && profile.errorMessage) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const enterProfileName = intl.formatMessage({
    id: 'profile.enterProfileName',
    defaultMessage: 'Enter profile name'
  });

  const enterProfileDescription = intl.formatMessage({
    id: 'profile.enterProfileDescription',
    defaultMessage: 'Enter profile Description'
  });

  const groupWidgets = getActiveWidgets(sortObjectKeys(groupByScreenName(widgetData.appWidgets)));

  return (
    <div className={s.profile}>
      <Banner
        failureText={messageText}
        showBanner={profile.showBanner}
        status={profile.isOpSuccessful}
        successText={messageText}
      />
      {isLoading && <LoadingSpinner />}
      <AdminControlsContentTemplate selectedPage="manageProfiles" userProfileWidgets={userProfileWidgets}>
        <div className={s.contentWrapper}>
          <div className={s.profileHeader}>
            <Link className={s.backLink} to={'/admin-controls/profile-list'}>
              <FormattedMessage id="profile.back" defaultMessage="< BACK TO PROFILES" />
            </Link>
            <h3>
              {isEdit ? isDuplicate ? (
                <FormattedMessage id="profile.profileDuplicate" defaultMessage="Duplicate Profile" />
              ) : (
                <FormattedMessage id="profile.profileEdit" defaultMessage="Edit Profile" />
              ) : (
                <FormattedMessage id="profile.addProfile" defaultMessage="Add Profile" />
              )}
            </h3>
          </div>
          <div className={s.profileContent}>
            {redirect ? <Navigate to="/admin-controls/profile-list" /> : ''}
            {isDuplicate && !isDuplicateAllowed ? <Navigate to="/admin-controls/profile-list" /> : ''}
            <Form>
              <Row>
                <Col lg={6}>
                  <Form.Group controlId="formProfileName">
                    <Form.Label>
                      <FormattedMessage id="profile.profileName" defaultMessage="Profile Name" />
                    </Form.Label>

                    <Form.Control
                      type="text"
                      placeholder={enterProfileName}
                      value={Name || ''}
                      name="Name"
                      onChange={onChange}
                      onBlur={updateProfileAttributes}
                      noValidate
                      className={`${formErrors && formErrors.Name ? s.formControlError : ''}`}
                    />
                    {formErrors && formErrors.Name && (
                      <p role="alert" className={s.error}>
                        {formErrors.Name}
                      </p>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group controlId="formProfileDescription">
                    <Form.Label>
                      <FormattedMessage id="profile.profileDescription" defaultMessage="Profile Description" />
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows="3"
                      placeholder={enterProfileDescription}
                      value={Description || ''}
                      name="Description"
                      onChange={onChange}
                      onBlur={updateProfileAttributes}
                      noValidate
                      className={`${s.textArea} ${formErrors && formErrors.Description ? s.formControlError : ''}`}
                    />
                    {formErrors && formErrors.Description && (
                      <p role="alert" className={s.error}>
                        {formErrors.Description}
                      </p>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="formProfileMFA" className={s.profileWidgetsContent}>
                    <label className={s.container}>
                      <FormattedMessage id="profile.EnableMFA" defaultMessage="Multi Factor Authentication (MFA)" />
                      <input
                        type="checkbox"
                        name="IsMFAEnabled"
                        id="IsMFAEnabled"
                        onChange={onChange}
                        checked={IsMFAEnabled || false}
                      />
                      <span className={s.checkmark} htmlFor="IsMFAEnabled"></span>
                    </label>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="formProfileIsActive" className={s.profileWidgetsContent}>
                    <label className={s.container}>
                      <FormattedMessage id="profile.profileActive" defaultMessage="Active" />
                      <input
                        type="checkbox"
                        name="IsActive"
                        id="IsActive"
                        onChange={onChange}
                        checked={IsActive || false}
                        onBlur={updateProfileAttributes}
                        noValidate
                        className={`${s.textArea} ${formErrors && formErrors.ProfileMFA ? s.formControlError : ''}`}
                      />
                      {formErrors && formErrors.ProfileMFA && (
                        <p role="alert" className={s.error}>
                          {formErrors.ProfileMFA}
                        </p>
                      )}
                      <span className={s.checkmark} htmlFor="IsActive"></span>
                    </label>
                  </Form.Group>
                </Col>
              </Row>
              {/* Profile Widget START */}

              <Form.Group controlId="formProfileWidgets">
                <Row>
                  <Col lg={6}>
                    <Form.Label>
                      <h4>
                        <FormattedMessage id="profile.widgetAssigned" defaultMessage="Widgets" />
                      </h4>
                    </Form.Label>
                    <div className={s.wigetInfo}>
                      <FormattedMessage
                        id="profile.widgetselection"
                        defaultMessage="Select the widgets that this profile will have access to."
                      />
                    </div>
                    <Row className="mt-3">
                      <Col lg={6} xs={12}>
                        <label className="form-label">
                          <FormattedMessage id="profile.search" defaultMessage="Search" />
                        </label>
                        <SearchBar searchHandler={searchHandler} />
                      </Col>
                      <Col lg={6} xs={12}>
                        <label className="form-label">
                          <FormattedMessage id="profile.filterBy" defaultMessage="Filter by" />
                        </label>
                        <Dropdown
                          id="drpWidgetCategory"
                          dataArray={screenNameOptions(groupWidgets)}
                          controlData={{
                            placeholderText: <FormattedMessage id="profile.selectPage" defaultMessage="Select page" />,
                            customClassName: ''
                          }}
                          onDropdownChange={e => onScreenNameChange(e)}
                          disabled={false}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <ProfileWidgetCategory
                      groupWidgets={groupWidgets}
                      getSelectedWidgets={getSelectedWidgets}
                      onWidgetChange={onWidgetChange}
                      s={s}
                    />
                  </Col>
                </Row>
              </Form.Group>
              {/* Profile Widget  END */}

              {(!profile || !profile.selectedProfile || !profile.selectedProfile.CreatedOrganisationId || profile.selectedProfile.CreatedOrganisationId == parseInt(currentOrganisationId) || isDuplicate) && <Button
                variant="primary"
                data-unittest="saveData"
                className={classNames(s.margin5, s.btnSaveChanges)}
                onClick={submitForm}
                noValidate
                disabled={profileInfo.IsDefault}
              >
                <FormattedMessage id="profile.profileSave" defaultMessage="Save" />
              </Button>}
              <Link to="/admin-controls/profile-list">
                <Button variant="outline-secondary" className={s.btnCancel}>
                  <FormattedMessage id="profile.profileCancel" defaultMessage="Cancel" />
                </Button>
              </Link>
            </Form>
          </div>
        </div>
      </AdminControlsContentTemplate>
    </div>
  );
};

Profile.propTypes = {
  saveProfile: PropTypes.func.isRequired,
  setSelectedProfile: PropTypes.func.isRequired,
  getSelectedProfile: PropTypes.func.isRequired,
  getAppWidgets: PropTypes.func.isRequired,
  userProfileWidgets: PropTypes.object.isRequired,
  profile: PropTypes.object,
  selectedListStateProfile: PropTypes.object,
  widgetData: PropTypes.object
};

export default Profile;